import type { A2uiComponentEntry } from 'types/space';
import type { SpaceHostMessage } from './types';

export interface SurfaceState {
  surfaceId: string | null;
  components: Record<string, A2uiComponentEntry>;
  root: string | null;
  dataModel: Record<string, unknown>;
  /** True once `root` is set AND a component with that id exists — the "buffer-until-root" gate (SC1). */
  mounted: boolean;
}

export const INITIAL_SURFACE_STATE: SurfaceState = {
  surfaceId: null,
  components: {},
  root: null,
  dataModel: {},
  mounted: false,
};

function indexById(entries: A2uiComponentEntry[]): Record<string, A2uiComponentEntry> {
  const byId: Record<string, A2uiComponentEntry> = {};
  entries.forEach((entry) => {
    byId[entry.id] = entry;
  });
  return byId;
}

/**
 * Applies one v1.0 message-loop message to surface state. Buffers renders until `root` resolves to
 * a known component; subsequent `updateComponents`/`updateDataModel` patch the mounted surface in
 * place so the host can incrementally update without remounting the tree (SC10).
 */
export function surfaceReducer(state: SurfaceState, message: SpaceHostMessage): SurfaceState {
  switch (message.type) {
    case 'createSurface': {
      const components = indexById(message.components);
      return {
        surfaceId: message.surfaceId,
        components,
        root: message.root,
        dataModel: message.dataModel ?? {},
        mounted: Boolean(message.root && components[message.root]),
      };
    }
    case 'updateComponents': {
      if (message.surfaceId !== state.surfaceId) return state;
      const patched = { ...state.components, ...indexById(message.components) };
      return {
        ...state,
        components: patched,
        mounted: state.mounted || Boolean(state.root && patched[state.root]),
      };
    }
    case 'updateDataModel': {
      if (message.surfaceId !== state.surfaceId) return state;
      return { ...state, dataModel: { ...state.dataModel, ...message.patch } };
    }
    case 'deleteSurface': {
      if (message.surfaceId !== state.surfaceId) return state;
      return INITIAL_SURFACE_STATE;
    }
    default:
      return state;
  }
}
